import { useCallback, useState } from 'react'
import { jsPDF } from 'jspdf'
import html2canvas from 'html2canvas'
import { saveAs } from 'file-saver'

const PDF_DIMENSION = {
  width: 595,
  height: 842,
  padding: 20,
}

export type PDFInfoType = {
  type: string,
  pageId: number,
}

const useJSPDF = () => {
  const [loading, setLoading] = useState<boolean>(false)

  const printDocument = useCallback(async (elementId: string) => {
    const input = document.getElementById(elementId)

    if (input) {
      setLoading(true)

      /** Visible the view for export  */
      input.style.display = 'block'
      const pdf = new jsPDF('portrait', 'px', [PDF_DIMENSION.width, PDF_DIMENSION.height])
      try {
        const canvas = await html2canvas(input, { useCORS: true })
        const imgData = canvas.toDataURL('image/png')

        const imgWidth = pdf.internal.pageSize.getWidth() - PDF_DIMENSION.padding * 2
        const imgHeight = (canvas.height * imgWidth) / canvas.width
        let heightLeft = imgHeight
        let position = PDF_DIMENSION.padding

        pdf.addImage(imgData, 'PNG', PDF_DIMENSION.padding, position, imgWidth, imgHeight)
        heightLeft -= PDF_DIMENSION.height - 2 * PDF_DIMENSION.padding
        while (heightLeft >= 0) {
          position = heightLeft - (imgHeight + PDF_DIMENSION.padding)
          pdf.addPage()
          pdf.addImage(imgData, 'PNG', PDF_DIMENSION.padding, position, imgWidth, imgHeight)
          heightLeft -= PDF_DIMENSION.height
        }
        const pdfData = pdf.output('blob')
        const blobURL = URL.createObjectURL(pdfData)
        window.open(blobURL, '_blank')
      } catch (error) {
        console.log(error)
      }

      /** Hide the view after export  */
      input.style.display = 'none'
      setLoading(false)

    }
  }, [])

  const toBase64 = async (url: string) => {
    const response = await fetch(url);
    const blob = await response.blob();
    const reader = new FileReader();
    reader.readAsDataURL(blob);
    const base64Data = await new Promise<string>((resolve, reject) => {
      reader.onloadend = () => {
        if (typeof reader.result === 'string') {
          resolve(reader.result);
        } else {
          reject(new Error('Failed to convert blob to base64'));
        }
      };
    })
    return base64Data
  }

  const printDocuments = async (elementIds: string[], callback?: (i: number) => void) => {
    setLoading(true)
    try {
      const pdf = new jsPDF('portrait', 'px', [PDF_DIMENSION.width, PDF_DIMENSION.height])
      const image = { type: 'jpeg' };
      const innerPageWidth = PDF_DIMENSION.width - PDF_DIMENSION.padding * 2;
      const innerPageHeight = PDF_DIMENSION.height - PDF_DIMENSION.padding * 2;
      for (let i = 0; i < elementIds.length; i++) {
        const elementId = elementIds[i]
        const input = document.getElementById(elementId)
        if (!input) continue;

        const images = input.getElementsByTagName('img');
        const imagePromises = Array.from(images).map(async (img) => {
          try {
            const imgUrl = `${img.src}?time=${new Date().getTime()}`;
            const base64 = await toBase64(imgUrl);
            img.src = base64
          } catch (error) {
            console.log(error)
          }
        });
        await Promise.all(imagePromises);

        const canvas = await html2canvas(input, { useCORS: true, allowTaint: true })
        const pxFullHeight = canvas.height;
        const pxPageHeight = Math.floor(canvas.width * (PDF_DIMENSION.height / PDF_DIMENSION.width));
        const nPages = Math.ceil(pxFullHeight / pxPageHeight);
        const pageHeight = innerPageHeight;

        const pageCanvas = document.createElement('canvas');
        const pageCtx = pageCanvas.getContext('2d');
        pageCanvas.width = canvas.width;
        pageCanvas.height = pxPageHeight;

        for (let page = 0; page < nPages; page++) {
          const w = pageCanvas.width;
          const h = pageCanvas.height;
          if (pageCtx) {
            pageCtx.fillStyle = 'white';
            pageCtx.fillRect(0, 0, w, h);
            pageCtx.drawImage(canvas, 0, page * pxPageHeight, w, h, 0, 0, w, h);
          }
          if (page > 0) pdf.addPage();
          const imgData = pageCanvas.toDataURL('image/' + image.type);
          pdf.addImage(imgData, image.type, PDF_DIMENSION.padding, PDF_DIMENSION.padding, innerPageWidth, pageHeight);
        }
        if (i < elementIds.length - 1) {
          pdf.addPage()
        }
        if (callback) {
          callback(i)
        }
      }

      const pdfData = pdf.output('blob')
      const isSafari = (window as any).safari !== undefined;
      if (isSafari) {
        saveAs(pdfData, 'kaecher_export.pdf')
      } else {
        const blobURL = URL.createObjectURL(pdfData)
        window.open(blobURL, '_blank')
      }

    } catch (error) {
      console.info(error)
    }
    setLoading(false)
  }

  const openNewPDFWindow = (pdfData: PDFInfoType[]) => {
    const popup = window.open(`${window.location.origin}/PDF?data=${JSON.stringify(pdfData)}`, 'newWin', 'width=' + screen.availWidth + ',height=' + screen.availHeight)
    if (!popup) {
      alert('Please allow popups for this website')
    }
  }

  return { printDocument, loading, printDocuments, openNewPDFWindow }
}

export default useJSPDF
